import React, { FC, useEffect, useMemo, useState } from 'react'
import isBefore from 'date-fns/isBefore'
import { StockType } from 'types/stock'
import { Spacer } from 'components/Utility'
import { Caution, SmallHeadline, SubDescription } from 'components/Typography'
import { dateToString, parseToDate } from 'lib/helper'
import { useSelectHoursToDeadline, useSelectNumber } from 'hooks/form'
import { HoursToDeliveryDeadlineForm, SelectBox } from 'components/Form'
import { NavButton, OrangeButton } from 'components/Button'
import { addMinutes, differenceInMinutes } from 'date-fns'
import { StoreDeliveryBreadType } from 'types/storeDeliveryBread'
import { useStockAmountOptions } from 'hooks/stock'
import { FirebaseTimestamp } from 'firebase/index'
import { getInitialDeliveryStock } from 'reducks/stock/operations'
import { BreadType } from 'types/bread'
import { useSelector } from 'react-redux'
import { State } from 'index'
import { getStoreRef } from 'reducks/auth/selectors'
import { getStoreName } from 'reducks/store/selectors'

type Props = {
  backToSelectDate: () => void
  bread: BreadType
  date: Date
  saveIndividualDeliveryStock: (individualStock: StockType) => void
  stockList: StockType[]
  storeDeliveryBread: StoreDeliveryBreadType
}

const IndividualDeliveryScheduleEditCard: FC<Props> = (props) => {
  const { backToSelectDate, bread, date, saveIndividualDeliveryStock, stockList, storeDeliveryBread } = props
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeName = useSelector((state: State) => getStoreName(state))
  const [maximumOrder, setMaximumOrder] = useState<number>(0)
  const { hoursToDeadline, setHoursToDeadline, selectDateToDeadline, selectTimeToDeadline } = useSelectHoursToDeadline()
  const [error, setError] = useState<string>('')
  const selectMaximumOrder = useSelectNumber(setMaximumOrder)

  const stock = useMemo(() => {
    const strDate = dateToString(date)
    const stockOfDate = stockList.find((stock) => stock.date === strDate)
    if (stockOfDate) return stockOfDate
    const stockRef = storeRef
      .collection('store_stocks')
      .doc(`stock_delivery_${storeDeliveryBread.ref?.id}-${strDate}_00:00`)
    return getInitialDeliveryStock(bread, bread.bread_ref!, strDate, stockRef, storeDeliveryBread, storeName, storeRef)
  }, [stockList, date, bread, storeRef, storeName, storeDeliveryBread])

  useEffect(() => {
    if (!stock) return
    // 在庫設定済みであれば既存値を設定
    const hoursToDeadline = differenceInMinutes(parseToDate(stock.date), stock.deadline.toDate()) / 60
    setHoursToDeadline(hoursToDeadline)
    setMaximumOrder(stock.stock)
  }, [stock])

  const maximumOrderOptions = useStockAmountOptions()

  return (
    <div>
      <SmallHeadline label={'注文締め切り'} required={true} />
      <Spacer size={8} />
      <SubDescription description={'お客さまが、アプリ上で購入できる締切日です。'} />
      <Spacer size={8} />
      <HoursToDeliveryDeadlineForm
        hoursToDeadline={hoursToDeadline}
        selectDateToDeadline={selectDateToDeadline}
        selectTimeToDeadline={selectTimeToDeadline}
      />
      <Spacer size={16} />
      <SmallHeadline label={'1日の販売上限数'} required={true} />
      <Spacer size={8} />
      <SubDescription description={'１日に販売できる上限を入力してください。'} />
      <Spacer size={8} />
      <SelectBox options={maximumOrderOptions} select={selectMaximumOrder} value={maximumOrder} />
      <Spacer size={16} />
      {error !== '' && (
        <>
          <Spacer size={8} />
          <Caution caution={error} />
        </>
      )}
      <Spacer size={16} />
      <OrangeButton
        label={'保存する'}
        onClick={() => {
          const deadline = addMinutes(date, -(hoursToDeadline * 60))
          if (isBefore(deadline, new Date())) {
            setError('注文締め切りが過ぎています。正しい日付に修正してください。')
          } else {
            setError('')
            stock.deadline = FirebaseTimestamp.fromDate(deadline)
            stock.stock = maximumOrder
            saveIndividualDeliveryStock(stock)
          }
        }}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={12} />
      <NavButton
        label={'日付の選択に戻る'}
        iconPath={'/icon/grey/arrow_prev.svg'}
        onClick={backToSelectDate}
        url={''}
      />
    </div>
  )
}

export default IndividualDeliveryScheduleEditCard
